import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, Paperclip, Square, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ChatScope } from "./scope";
import { getSuggestedPromptCategories } from "./suggested-prompts";
import type { SuggestedPromptItem } from "./suggested-prompts";

interface Props {
  scope: ChatScope;
  onSend: (text: string) => void;
  onStop?: () => void;
  onAttachSession: () => void;
  busy: boolean;
  /** True specifically while the AI is streaming a response (subset of busy). */
  isStreaming?: boolean;
  /** At least one user + one assistant message — for prompts that need prior dialogue. */
  canSuggestFromThread?: boolean;
  /** Uppercase toolkit slugs for connected Composio apps, e.g. ["GOOGLECALENDAR", "GMAIL"]. */
  connectedAppIds?: string[];
  /** When `nonce` changes, replaces the draft and focuses the input (e.g. empty-state starters). */
  composerPrefill?: { text: string; nonce: number } | null;
  /** Hide the member-only "attach test result" control (e.g. on the dashboard landing surface). */
  hideAttach?: boolean;
  /** Larger, more balanced presentation for use as a landing-page hero. */
  landing?: boolean;
}

export function ChatComposer({
  scope,
  onSend,
  onStop,
  onAttachSession,
  busy,
  isStreaming = false,
  canSuggestFromThread = false,
  connectedAppIds = [],
  composerPrefill = null,
  hideAttach = false,
  landing = false,
}: Props) {
  const { t } = useTranslation("intelligence");
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!composerPrefill) return;
    setValue(composerPrefill.text);
    queueMicrotask(() => ref.current?.focus());
  }, [composerPrefill?.nonce]);

  // Grow with the text, capped. An empty box keeps its CSS min-height rather
  // than a measured one: measuring while the docked rail is still animating
  // open (44px wide) wraps the placeholder into many lines and would lock the
  // box at the cap. Re-measure on width changes for the same reason.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      if (!el.value) {
        el.style.height = "";
        return;
      }
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(el.parentElement ?? el);
    return () => observer.disconnect();
  }, [value]);

  const handleSend = () => {
    const text = value.trim();
    if (!text || busy) return;
    setValue("");
    onSend(text);
  };

  const isMember = scope.kind === "member";

  function isItemDisabled(item: SuggestedPromptItem): boolean {
    if (item.requiresThread && !canSuggestFromThread) return true;
    if (item.requiresApp && !connectedAppIds.includes(item.requiresApp))
      return true;
    if (item.requiresScope && item.requiresScope !== scope.kind) return true;
    return false;
  }

  function itemDisabledTitle(item: SuggestedPromptItem): string | undefined {
    if (item.requiresThread && !canSuggestFromThread)
      return "Skriv några meddelanden först så att samtalet finns som underlag";
    if (item.requiresApp && !connectedAppIds.includes(item.requiresApp))
      return `Kräver att ${item.requiresApp} är ansluten under Inställningar`;
    if (item.requiresScope && item.requiresScope !== scope.kind) {
      const labels: Record<string, string> = {
        member: "en teammedlem",
        team: "ett team",
        business: "organisationen",
      };
      return `Endast tillgänglig när du chattar om ${labels[item.requiresScope] ?? item.requiresScope}`;
    }
    return undefined;
  }

  return (
    <div className={landing ? "" : "pt-6"}>
      <div
        className={cn(
          "relative z-10 mx-auto w-full px-4",
          landing ? "max-w-[640px]" : "max-w-[760px]",
        )}
      >
        <div
          className={cn(
            "rounded-2xl border bg-background shadow-md focus-within:ring-2 focus-within:ring-primary/30",
            landing && "shadow-lg",
          )}
        >
          <Textarea
            ref={ref}
            rows={landing ? 2 : 1}
            value={value}
            placeholder={t("chat.composer.placeholder")}
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className={cn(
              "resize-none border-0 bg-transparent shadow-none focus-visible:ring-0",
              landing
                ? "min-h-[84px] px-5 pt-4 text-base"
                : "min-h-[44px] px-4 pt-3 text-sm",
            )}
          />

          <div
            className={cn(
              "flex items-center gap-1",
              landing ? "px-3 pb-3" : "px-2 pb-2",
            )}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-7 gap-1.5 rounded-full px-2 text-xs text-muted-foreground"
                  aria-label={t("chat.composer.suggestionsLabel")}
                  disabled={busy}
                  type="button"
                >
                  <Zap className="h-3.5 w-3.5" />
                  {t("chat.composer.suggestionsLabel")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-80">
                {getSuggestedPromptCategories(t).map((category, catIdx) => (
                  <div key={category.key}>
                    {catIdx > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-xs">
                      {category.label}
                    </DropdownMenuLabel>
                    {category.items.map((item) => {
                      const disabled = isItemDisabled(item);
                      return (
                        <DropdownMenuItem
                          key={item.id}
                          disabled={disabled}
                          title={itemDisabledTitle(item)}
                          className="cursor-pointer flex-col items-start gap-0.5 whitespace-normal"
                          onClick={() => {
                            if (disabled) return;
                            setValue(item.prompt);
                            queueMicrotask(() => ref.current?.focus());
                          }}
                        >
                          <span className="text-sm font-medium leading-snug">
                            {item.label}
                          </span>
                          <span className="text-xs text-muted-foreground leading-snug">
                            {item.description}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {hideAttach ? null : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-7 gap-1.5 rounded-full px-2 text-xs text-muted-foreground"
                    aria-label={t("chat.composer.attachLabel")}
                    disabled={busy}
                    type="button"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    {t("chat.composer.attachLabel")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel className="text-xs">
                    {t("chat.composer.addTestResult")}
                  </DropdownMenuLabel>
                  {isMember ? (
                    <DropdownMenuItem onClick={onAttachSession}>
                      {t("chat.composer.newSession")}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem disabled>
                      {t("chat.composer.noMemberForTest")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <div className="ml-auto">
              {isStreaming && onStop ? (
                <Button
                  size="icon"
                  onClick={onStop}
                  className="h-8 w-8 rounded-full"
                  aria-label={t("chat.stop")}
                  type="button"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!value.trim() || busy}
                  className="h-8 w-8 rounded-full"
                  aria-label={t("chat.composer.send")}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUp className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {!landing && (
        <div className="bg-card py-2 text-center">
          <p className="text-[11px] text-muted-foreground">
            {t("chat.disclaimer")}
          </p>
        </div>
      )}
    </div>
  );
}
